import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { StatusBadge } from '../../common';
import { useDialog } from '../../../context/DialogContext';
import { useViewMode } from '../../../context/ViewModeContext';
import { useInviteCampaign } from '../../../hooks/useInvites';
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard';
import { campaignStatusBadge } from './campaignLinks';
import { CampaignStepRail } from './CampaignStepRail';
import { campaignStepsFor, resolveCampaignStep, type CampaignStepId } from './campaignSteps';
import { CampaignDetailsStep } from './CampaignDetailsStep';
import { CampaignDesignStep } from './CampaignDesignStep';
import { CampaignQuestionsStep } from './CampaignQuestionsStep';
import { CampaignGuestsStep } from './CampaignGuestsStep';
import { CampaignSendStep } from './CampaignSendStep';
import { CampaignResultsStep } from './CampaignResultsStep';
import type { CampaignEventInfo } from './InvitePreview';

export interface CampaignWorkspaceProps {
  eventId: string;
  campaignId: string;
  /** The client portal: the office's questions-and-meals step and tools are left out. */
  clientMode?: boolean;
  /** The event as the preview shows it. */
  event?: CampaignEventInfo;
  /** The step in the address; anything else opens where the campaign stands. */
  step?: string | null;
  onStepChange: (step: CampaignStepId) => void;
  onBack: () => void;
  /**
   * Told whenever the open step gains or loses unsaved changes, so a page
   * around the workspace can ask before its own navigation closes it.
   */
  onDirtyChange?: (dirty: boolean) => void;
}

const DISCARD_PROMPT = {
  message: 'Leave this step without saving your changes?',
  variant: 'warning' as const,
  confirmLabel: 'Discard changes',
};

/**
 * One invitation campaign, worked through as steps — details, design,
 * questions and meals, guests, review and send, results — in place of the
 * modal that held them all in one scroll. The same page for the office and a
 * client with `canManageInvites`, with what the client may not do left out.
 */
export function CampaignWorkspace({
  eventId,
  campaignId,
  clientMode = false,
  event,
  step,
  onStepChange,
  onBack,
  onDirtyChange: reportDirty,
}: CampaignWorkspaceProps) {
  const { confirm } = useDialog();
  const { isMobile } = useViewMode();
  const navigate = useNavigate();
  const { data: campaign, isLoading, isError } = useInviteCampaign(campaignId);
  const [dirty, setDirty] = useState(false);
  const onDirtyChange = useCallback((value: boolean) => setDirty(value), []);

  useEffect(() => {
    reportDirty?.(dirty);
  }, [dirty, reportDirty]);
  // Closing or reloading the tab asks the browser's own question.
  useUnsavedChangesGuard(dirty);
  // A link anywhere on the page — the workspace's tabs, the sidebar — would
  // unmount the step and lose the draft, so while there is one it asks first.
  useEffect(() => {
    if (!dirty) return;
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const link = (e.target as Element | null)?.closest?.('a[href]');
      if (!(link instanceof HTMLAnchorElement) || (link.target && link.target !== '_self')) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      e.preventDefault();
      e.stopPropagation();
      void confirm(DISCARD_PROMPT).then((ok) => {
        if (!ok) return;
        setDirty(false);
        navigate(url.pathname + url.search + url.hash);
      });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [dirty, confirm, navigate]);

  const leave = async (then: () => void) => {
    if (dirty) {
      const ok = await confirm(DISCARD_PROMPT);
      if (!ok) return;
      setDirty(false);
    }
    then();
  };

  if (isLoading) {
    return (
      <div className="space-y-3" aria-busy="true">
        <div className="h-8 w-1/3 animate-pulse rounded bg-gray-100" />
        <div className="h-64 animate-pulse rounded-2xl bg-gray-100" />
      </div>
    );
  }
  if (isError || !campaign) {
    return (
      <div className="rounded-2xl border border-gray-200 p-6 text-center">
        <p className="text-sm text-gray-700">This invitation could not be found.</p>
        <button
          type="button"
          onClick={onBack}
          className="mt-3 text-sm font-medium text-primary-700 hover:text-primary-800"
        >
          Back to the invitations
        </button>
      </div>
    );
  }

  const steps = campaignStepsFor(clientMode);
  const active = resolveCampaignStep(step, clientMode, campaign.status === 'SENT');
  const nextOf = (id: CampaignStepId) => {
    const index = steps.findIndex((s) => s.id === id);
    const next = steps[index + 1];
    return next ? () => onStepChange(next.id) : undefined;
  };
  const badge = campaignStatusBadge(campaign);
  const stepProps = {
    campaign,
    eventId,
    clientMode,
    onDirtyChange,
    onNext: nextOf(active),
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void leave(onBack)}
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          All invitations
        </button>
        <h2 className="min-w-0 flex-1 truncate text-lg font-semibold text-gray-900">
          {campaign.name}
        </h2>
        <StatusBadge label={badge.label} color={badge.color} />
        <span className="text-sm text-gray-500">
          {campaign.recipientCount ?? 0} guest{campaign.recipientCount === 1 ? '' : 's'}
        </span>
      </div>

      <CampaignStepRail
        steps={steps}
        active={active}
        stacked={isMobile}
        onSelect={(id) => {
          if (id === active) return;
          void leave(() => onStepChange(id));
        }}
        done={{
          details: !!(campaign.subject || campaign.message),
          design: !!campaign.designId,
          guests: (campaign.recipientCount ?? 0) > 0,
          send: campaign.status === 'SENT',
          results: false,
        }}
      >
        {active === 'details' && <CampaignDetailsStep key={active} {...stepProps} />}
        {active === 'design' && <CampaignDesignStep key={active} {...stepProps} event={event} />}
        {active === 'questions' && <CampaignQuestionsStep key={active} {...stepProps} />}
        {active === 'guests' && <CampaignGuestsStep key={active} {...stepProps} />}
        {active === 'send' && <CampaignSendStep key={active} {...stepProps} />}
        {active === 'results' && <CampaignResultsStep key={active} {...stepProps} />}
      </CampaignStepRail>
    </div>
  );
}
